import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { Opportunity, OpportunityStatus } from '../client-actions.model';

const PRIORITY_OPTIONS = [
  { label: 'High', value: 'High' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Low', value: 'Low' },
];

const SOURCE_TYPE_OPTIONS = [
  { label: 'AI Agent', value: 'aiAgent' },
  { label: 'Human', value: 'human' },
];

const STATUS_OPTIONS: { label: string; value: OpportunityStatus }[] = [
  { label: 'Potential', value: 'Potential' },
  { label: 'Committed', value: 'Committed' },
  { label: 'Completed', value: 'Completed' },
  { label: 'Withdrawn', value: 'Withdrawn' },
];

@Component({
  selector: 'bai-opportunity-add-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Button, InputTextModule, SelectModule],
  template: `
    <div class="flex flex-col gap-4 p-2">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="opp-desc">
          Description <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="opp-desc"
          pInputText
          [(ngModel)]="form().opportunityDescription"
          (ngModelChange)="patchForm('opportunityDescription', $event)"
          placeholder="Describe the opportunity"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().opportunityDescription) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="opp-source">
          Source <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="opp-source"
          pInputText
          [(ngModel)]="form().source"
          (ngModelChange)="patchForm('source', $event)"
          placeholder="e.g. Annual Review, Client Meeting"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().source) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="opp-source-type">
            Source Type <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <p-select
            inputId="opp-source-type"
            [options]="sourceTypeOptions"
            [(ngModel)]="form().sourceType"
            (ngModelChange)="patchForm('sourceType', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="Select type"
            styleClass="w-full"
            aria-required="true"
          />
          @if (submitted() && !form().sourceType) {
            <span class="text-xs text-red-500" role="alert">Required</span>
          }
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="opp-priority">
            Priority <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <p-select
            inputId="opp-priority"
            [options]="priorityOptions"
            [(ngModel)]="form().priority"
            (ngModelChange)="patchForm('priority', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="Select priority"
            styleClass="w-full"
            aria-required="true"
          />
          @if (submitted() && !form().priority) {
            <span class="text-xs text-red-500" role="alert">Required</span>
          }
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="opp-status">
          Status <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <p-select
          inputId="opp-status"
          [options]="statusOptions"
          [(ngModel)]="form().status"
          (ngModelChange)="patchForm('status', $event)"
          optionLabel="label"
          optionValue="value"
          placeholder="Select status"
          styleClass="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().status) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <p-button label="Cancel" severity="secondary" (onClick)="cancel()" />
        <p-button label="Add" (onClick)="submit()" />
      </div>
    </div>
  `,
})
export class OpportunityAddDialogComponent {
  private _ref = inject(DynamicDialogRef);

  readonly priorityOptions = PRIORITY_OPTIONS;
  readonly sourceTypeOptions = SOURCE_TYPE_OPTIONS;
  readonly statusOptions = STATUS_OPTIONS;
  readonly submitted = signal(false);

  readonly form = signal<Partial<Omit<Opportunity, 'id' | 'createdDate' | 'updatedDate'>>>({
    opportunityDescription: '',
    source: '',
    sourceType: undefined,
    priority: undefined,
    status: 'Potential',
  });

  patchForm(key: string, value: unknown): void {
    this.form.update((f) => ({ ...f, [key]: value }));
  }

  cancel(): void {
    this._ref.close(null);
  }

  submit(): void {
    this.submitted.set(true);
    const f = this.form();
    if (!f.opportunityDescription || !f.source || !f.sourceType || !f.priority || !f.status) return;

    const today = new Date().toISOString().slice(0, 10);
    const result: Omit<Opportunity, 'id'> = {
      opportunityDescription: f.opportunityDescription,
      source: f.source,
      sourceType: f.sourceType,
      priority: f.priority,
      status: f.status,
      createdDate: today,
      updatedDate: today,
    };
    this._ref.close(result);
  }
}
